import { useEffect, useState } from "react";
import "./ProfilePage.scss";
import {
  Avatar,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser, clearError, updateUserNames, logoutUser } from "../../../slices/userSlice";
import { toast } from "react-toastify";
import { logout } from "../../../slices/userSlice";
import { useNavigate } from "react-router-dom";
import FilterRoundedIcon from '@mui/icons-material/FilterRounded';

// Helper to test `go()` logic
export const goHelper = (navigate, isAuthenticated) => (path, requireAuth = true) => {
  if (requireAuth && !isAuthenticated) {
    navigate("/login");
  } else {
    navigate(path);
  }
};



export default function ProfilePage() {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const { user, loading, error, isAuthenticated } = useSelector((state) => state.user);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profilePictureURL, setProfilePictureURL] = useState('');
  const [isPhotoModalOpen, setPhotoModalOpen] = useState(false);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [imagePreviewLoaded, setImagePreviewLoaded] = useState(false);
  const go = goHelper(navigate, isAuthenticated);

  const handleToggle = async () => {
    if (isEditing) {
      const currentErrors = {
        firstName: validateField("firstName", firstName),
        lastName: validateField("lastName", lastName),
        phoneNumber: validateField("phoneNumber", phoneNumber),
        profilePictureURL: validateField("profilePictureURL", profilePictureURL),
      };

      setErrors(currentErrors);

      if (Object.values(currentErrors).some(Boolean)) {
        return;
      }

      if (!hasChanges) {
        setIsEditing(false);
        return;
      }

      const userData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.replace(/\D/g, ""),
        profilePictureURL: profilePictureURL ? profilePictureURL.trim() : "",
      };

      try {
        await dispatch(updateUserNames(userData)).unwrap();
        await dispatch(fetchUser());
        setPhotoModalOpen(false);
        toast.success("Changes have been saved!");
      } catch (error) {
        toast.error("An error has occurred, please try again later.");
      }
    }

    setIsEditing(!isEditing);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "phoneNumber") {
      nextValue = formatPhoneNumber(value);
      setPhoneNumber(nextValue);
    }
    if (name === "firstName") setFirstName(nextValue);
    if (name === "lastName") setLastName(nextValue);
    if (name === "profilePictureURL") {
        setProfilePictureURL(value);
        setImagePreviewError(false);
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, nextValue, { [name]: nextValue }),
    }));
  };
  const [errors, setErrors] = useState({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      profilePictureURL: "",
    });
  const handleClose = () => {
   setProfilePictureURL(user?.profilePictureURL || '');
   setImagePreviewError(false);
   setImagePreviewLoaded(false);
   setErrors((prev) => ({
    ...prev,
    profilePictureURL: ""
  }));
   setPhotoModalOpen(false); 
  }

  const hasValidationErrors = Object.values(errors).some(
    error => error && error.length > 0
  );
  const isFormValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    !hasValidationErrors;
  const normalizeEmail = (email) => (email || "").trim().toLowerCase();

  const validateField = (name, value, nextState = {}) => {
    const currentFirstName = nextState.firstName ?? firstName;
    const currentLastName = nextState.lastName ?? lastName;
    switch (name) {
      case "firstName":
        if (value.trim() === "") return "This field is required";
        if (value.length > 50) return "Maximum 50 characters allowed";
        break;

      case "lastName":
        if (value.trim() === "") return "This field is required";
        if (value.length > 50) return "Maximum 50 characters allowed";
        break;

      case "phoneNumber": {
        const digits = value.replace(/\D/g, "");
        if (digits.length > 0 && digits.length < 10) {
          return "Phone number must be 10 digits";
        }
        break;
      }

      case "profilePictureURL": {
        if (value.length >= 2000) return "Maximum 2000 characters allowed";
         if (value.trim() !== "") {
        try {
          const url = new URL(value);

          if (!["http:", "https:"].includes(url.protocol)) {
            return "URL must start with http:// or https://";
          }
        } catch {
          return "Please enter a valid URL";
        }
      }
        break;
      }

      default:
        return "";
    }

    return "";
  };
const hasChanges =
  (firstName || '').trim() !== (user?.firstName || '').trim() ||
  (lastName || '').trim() !== (user?.lastName || '').trim() ||
  phoneNumber.replace(/\D/g, '') !== (user?.phoneNumber || '') ||
  (profilePictureURL || '').trim() !== (user?.profilePictureURL || '').trim();

  useEffect(() => {
    if (!user) {
      dispatch(fetchUser());
    } else {
      if (!isEditing && !isPhotoModalOpen) {
        setFirstName(user.firstName || '');
        setLastName(user.lastName || '');
        setPhoneNumber(formatPhoneNumber(user.phoneNumber) || '');
        setProfilePictureURL(user.profilePictureURL || '');
      }
    }
  }, [dispatch, user, isEditing, isPhotoModalOpen]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    // Get body element and add class on mount
    document.body.classList.add("profile-active");

    return () => {
      // Cleanup: Remove the class when the component unmounts
      document.body.classList.remove("profile-active");
    };
  }, []); // run once, on mount and cleanup on unmount

  if (loading && !user) {
    return (
      <div className="profile-page loading" data-testid="profile-loading">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page" data-testid="profile-error">
        <Alert severity="error" data-testid="profile-error-alert">Unable to load profile. Please log in again.</Alert>
      </div>
    );
  }

  const formatPhoneNumber = (value) => {
    if (!value) return value;

    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0,3)}) ${phoneNumber.slice(3)}`
    }

    return `(${phoneNumber.slice(0,3)}) ${phoneNumber.slice(3,6)}-${phoneNumber.slice(6,10)}`
  }

  const textFieldSx = {
    width: '100%',
    position: 'relative',
    '& .MuiInputBase-root': {
      minHeight: '28px',
      display: 'flex',
      borderBottom: '1px solid #6e2e76',
    },
    '& .MuiInputBase-input': {
      padding: 0,
      fontSize: '12px',
      lineHeight: '1.4',
    },
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      top: '100%',
      left: 0,
      margin: 0,
      fontSize: '11px',
    },
  };
  const formatRole = (authority) => {
  if (!authority) return "";

  return authority
    .replace("ROLE_", "")
    .toLowerCase()
    .replace(/^./, (char) => char.toUpperCase());
};

 return (
  <div className="profile-page" data-testid="profile-page">
    {/* Profile Picture Dialog */}
    <Dialog
      open={isPhotoModalOpen}
      onClose={handleClose}
      data-testid="update-avatar-dialog"
    >
      <DialogTitle>Update Profile Picture</DialogTitle>

      <DialogContent
        sx={{
          minWidth: "250px",
          maxWidth: "300px",
          minHeight: "107px",
          pt: 1,
        }}
      >
        <TextField
          data-testid="update-avatar"
          fullWidth
          label="Image URL"
          variant="outlined"
          value={profilePictureURL}
          name="profilePictureURL"
          onChange={handleChange}
          inputProps={{ maxLength: 2000 }}
          error={!!errors.profilePictureURL}
          helperText={errors.profilePictureURL}
          placeholder="https://example.com/image.jpg"
          sx={{ mt: 1 }}
        />

        {profilePictureURL && !errors.profilePictureURL && (
          <Avatar
            className="image-preview-avatar"
            data-testid="image-preview"
            src={profilePictureURL}
            onError={() => {
              setImagePreviewError(true);
              setImagePreviewLoaded(false);
            }}
            onLoad={() => {
              setImagePreviewLoaded(true);
              setImagePreviewError(false);
            }}
          >
            <PersonIcon />
          </Avatar>
        )}

        {imagePreviewError && (
          <p className="image-preview-error">
            Image could not be loaded
          </p>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>

        <Button
          disabled={
            !imagePreviewLoaded ||
            !!errors.profilePictureURL
          }
          variant="contained"
          onClick={async () => {
            const userData = {
              firstName,
              lastName,
              phoneNumber: phoneNumber.replace(/\D/g, ""),
              profilePictureURL,
            };

            try {
              await dispatch(updateUserNames(userData)).unwrap();
              await dispatch(fetchUser());

              setPhotoModalOpen(false);

              toast.success("Profile picture updated!");
            } catch (err) {
              dispatch(clearError());
            }
          }}
          sx={{
            bgcolor: "#6e2e76",
            "&:hover": {
              bgcolor: "#5a2561",
            },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>

    {/* Main Profile Content */}
    <main
      className="profile-content"
      data-testid="profile-main-content"
    >
      {/* Page Heading */}
      <div className="profile-page-heading">
        <h1>Profile</h1>
      
      </div>

      {/* Profile Card */}
      <section className="profile-card">
        {/* User Header */}
        <div className="profile-user-header">
          <div className="profile-avatar-wrapper">
            <Avatar
              src={profilePictureURL || user?.profilePictureURL}
              onClick={() => setPhotoModalOpen(true)}
              className="profile-avatar"
              data-testid="profile-avatar"
            >
              <PersonIcon className="profile-person-icon" />
            </Avatar>

            <button
              type="button"
              className="profile-camera-button"
              onClick={() => setPhotoModalOpen(true)}
              aria-label="Update profile picture"
            >
              <FilterRoundedIcon
                data-testid="profile-picture-icon"
              />
            </button>
          </div>

          <div
            className="profile-user-text"
            data-testid="profile-user-info"
          >
            <h2 data-testid="profile-user-name">
              {firstName} {lastName}
            </h2>

            <p data-testid="profile-user-role">
            {formatRole(user?.roles?.[0]?.authority)}
            </p>
          </div>
        </div>

        <div className="profile-divider" />

        {/* Information Header */}
        <div className="information-heading">
          <div className="information-icon">
            <PersonIcon />
          </div>

          <h3>Personal Information</h3>

          <button
            className="edit-btn"
            onClick={handleToggle}
            disabled={!isFormValid}
          >
            {isEditing ? "Save" : "Edit"}
          </button>
        </div>

        {/* Information Fields */}
        <div className="profile-info">
          {/* First Name */}
          <div className="input-row">
            <label>First Name</label>

            {isEditing ? (
              <TextField
                hiddenLabel
                name="firstName"
                aria-label="first Name"
                value={firstName}
                error={!!errors.firstName}
                helperText={errors.firstName}
                variant="standard"
                onChange={handleChange}
                slotProps={{
                  input: {
                    "data-testid": "firstNameInput",
                  },
                  htmlInput: {
                    maxLength: 50,
                  },
                }}
                sx={textFieldSx}
              />
            ) : (
              <p data-testid="profile-first-name">
                {firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="input-row">
            <label>Last Name</label>

            {isEditing ? (
              <TextField
                hiddenLabel
                aria-label="last Name"
                name="lastName"
                value={lastName}
                error={!!errors.lastName}
                helperText={errors.lastName}
                variant="standard"
                onChange={handleChange}
                sx={textFieldSx}
                slotProps={{
                  input: {
                    "data-testid": "lastNameInput",
                  },
                  htmlInput: {
                    maxLength: 50,
                  },
                }}
              />
            ) : (
              <p data-testid="profile-last-name">
                {lastName}
              </p>
            )}
          </div>

          {/* Phone Number */}
          <div className="input-row phone-row">
            <label>Phone Number</label>

            {isEditing ? (
              <TextField
                hiddenLabel
                aria-label="phone number"
                name="phoneNumber"
                value={phoneNumber}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                variant="standard"
                onChange={handleChange}
                sx={textFieldSx}
                slotProps={{
                  input: {
                    "data-testid": "phoneNumberInput",
                  },
                  htmlInput: {
                    maxLength: 20,
                  },
                }}
              />
            ) : (
              <p data-testid="profile-phone-number">
                {formatPhoneNumber(phoneNumber) || (
                  <span className="not-provided">
                    Not Provided
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      </section>
    </main>
  </div>
)};